import { useEffect, useRef, useState } from "react";
import "./gallery.css";
import Instagram from "../assets/ig.svg";

import Image1 from "../assets/1.webp";
import Image2 from "../assets/2.webp";
import Image3 from "../assets/3.webp";
import Image4 from "../assets/4.webp";
import Image5 from "../assets/5.webp";
import Image6 from "../assets/6.webp";
import { GalleryList } from "./GalleryList";

type CarouselProps = {
  autoPlay?: boolean;
  interval?: number;
};

const images = [
  Image1,
  Image2,
  Image3,
  Image4,
  Image5,
  Image6,
];

export function Gallery({
  autoPlay = true,
  interval = 3000,
}: CarouselProps) {
  const [index, setIndex] = useState<number>(1);
  const [transition, setTransition] = useState<boolean>(true);

  const isAnimating = useRef(false);

  const trackRef = useRef<HTMLDivElement | null>(null);
  const startX = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const extendedImages = [
    images[images.length - 1],
    ...images,
    images[0],
  ];

  const next = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setIndex((prev) => prev + 1);
  };

  const prev = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    setIndex((prev) => prev - 1);
  };

  // autoplay
  useEffect(() => {
    if (!autoPlay) return;

    autoPlayRef.current = setInterval(() => {
      next();
    }, interval);

    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [index, autoPlay, interval]);

  const pauseAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
  };


  useEffect(() => {
    const node = trackRef.current;
    if (!node) return;

    const handleTransitionEnd = () => {
      if (index === extendedImages.length - 1) {
        setTransition(false);
        setIndex(1);
      } else if (index === 0) {
        setTransition(false);
        setIndex(extendedImages.length - 2);
      }

      isAnimating.current = false;
    };

    node.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      node.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [index, extendedImages.length]);

  useEffect(() => {
    if (!transition) {
      requestAnimationFrame(() => {
        setTransition(true);
      });
    }
  }, [transition]);

  // swipe
  const handleStart = (x: number) => {
    startX.current = x;
    isDragging.current = true;
    pauseAutoPlay();
  };

  const handleEnd = (x: number) => {
    if (!isDragging.current) return;

    const diff = startX.current - x;

    if (diff > 50) next();
    else if (diff < -50) prev();

    isDragging.current = false;
  };

  return (
    <section id="gallery" className="section">
      <div className="gallery container">
        <h3 className="trabajos">Nuestro equipo</h3>
        <div
          className="carousel"
          onMouseEnter={pauseAutoPlay}
          onMouseLeave={() => autoPlay && next()}
        >
          <div
            className="carousel-track"
            ref={trackRef}
            style={{
              transform: `translateX(-${index * 100}%)`,
              transition: transition ? "transform 0.4s ease" : "none",
            }}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
              handleStart(e.clientX)
            }
            onMouseUp={(e: React.MouseEvent<HTMLDivElement>) =>
              handleEnd(e.clientX)
            }
            onTouchStart={(e: React.TouchEvent<HTMLDivElement>) =>
              handleStart(e.touches[0].clientX)
            }
            onTouchEnd={(e: React.TouchEvent<HTMLDivElement>) =>
              handleEnd(e.changedTouches[0].clientX)
            }
          >
            {extendedImages.map((src, i) => (
              <img key={i} src={src} className="carousel-image" loading="lazy" decoding="async" />
            ))}
          </div>

          <button className="carousel-btn left" onClick={prev}>
            ‹
          </button>
          <button className="carousel-btn right" onClick={next}>
            ›
          </button>

          <div className="dots">
            {images.map((_, i) => (
              <span
                key={i}
                className={i === index - 1 ? "dot active" : "dot"}
                onClick={() => setIndex(i + 1)}
              />
            ))}
          </div>
        </div>
        <GalleryList Instagram={Instagram} />
      </div>
    </section>
  );
}